import type { PlayerSeasonStatRow } from '../../services/player/playerSeasonStats'
import { toStatText } from './statText'

export interface StatCell {
  // Column label, e.g. "MP", "G", "GFA".
  abbr: string
  // Display value, stringified (the API may send a raw number).
  value: string
}

export interface TeamSeasonStats {
  teamId?: string
  teamName: string
  // The raw team object from the stats response, passed straight to
  // getImageThumbnailId (same as the profile stats table) so the logo resolves.
  team: unknown
  // Fallback logo source: the matching player-team's `image` (a bare filename)
  // used when the stats team object carries no image. Shaped as { image } so it
  // can be handed to getImageThumbnailId directly.
  logoFallback?: { image: unknown }
  seasonName?: string
  // Every stat the API returned for this team/season, in API order.
  cells: StatCell[]
}

// A minimal player-team shape for the logo fallback lookup.
export interface PlayerTeamLite {
  id?: string
  image?: unknown
}

// A value is "countable" (summable across teams) when it parses as an integer
// with no decimal point. Averages (GFA "0.33", PTS/G) contain a decimal and are
// excluded from the career summary — summing them would be meaningless.
const isCountable = (value: string): boolean => /^-?\d+$/.test(value)

const cellsOf = (row: PlayerSeasonStatRow): StatCell[] =>
  (row.stats ?? [])
    .map((s) => ({
      abbr: toStatText(s.type?.abbr ?? s.abbr),
      value: toStatText(s.value),
    }))
    .filter((c) => c.abbr !== '')

/**
 * Build the per-team stat rows (one per row, all cells in API order).
 * `playerTeams` (from GET /players/:id) supplies a logo fallback keyed by team
 * id — the stats response sometimes omits the team image while the player's
 * teams carry it.
 */
export const getTeamSeasonStats = (
  rows: PlayerSeasonStatRow[],
  playerTeams: PlayerTeamLite[] = []
): TeamSeasonStats[] => {
  const imageByTeamId = new Map<string, unknown>()
  for (const t of playerTeams) {
    if (t?.id && t.image != null) {
      imageByTeamId.set(t.id, t.image)
    }
  }

  return (rows ?? []).map((row) => {
    const team = (row.team ?? {}) as {
      id?: string
      name_full?: string
      name?: string
      image?: unknown
    }
    const fallbackImage = team.id ? imageByTeamId.get(team.id) : undefined
    return {
      teamId: team.id,
      teamName: team.name_full || team.name || '',
      team: row.team,
      logoFallback:
        fallbackImage != null ? { image: fallbackImage } : undefined,
      seasonName: row.season?.name,
      cells: cellsOf(row),
    }
  })
}

/**
 * Build the career summary: for each countable stat abbr, sum its value across
 * every team/season, preserving first-seen column order. Average-type stats
 * (decimal values) are skipped entirely.
 */
export const getCareerSummary = (rows: PlayerSeasonStatRow[]): StatCell[] => {
  const order: string[] = []
  const totals = new Map<string, number>()

  for (const row of rows ?? []) {
    for (const cell of cellsOf(row)) {
      if (!isCountable(cell.value)) {
        continue
      }
      if (!totals.has(cell.abbr)) {
        totals.set(cell.abbr, 0)
        order.push(cell.abbr)
      }
      totals.set(
        cell.abbr,
        (totals.get(cell.abbr) as number) + Number(cell.value)
      )
    }
  }

  return order.map((abbr) => ({
    abbr,
    value: String(totals.get(abbr) ?? 0),
  }))
}
